import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Alert,
  Button,
  Card,
  CardContent,
  CardMedia,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  FormLabel,
  Chip,
  Container,
  Divider,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography
} from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded';
import PlayCircleOutlineRoundedIcon from '@mui/icons-material/PlayCircleOutlineRounded';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import MilitaryTechRoundedIcon from '@mui/icons-material/MilitaryTechRounded';
import SpaRoundedIcon from '@mui/icons-material/SpaRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import BookmarkBorderRoundedIcon from '@mui/icons-material/BookmarkBorderRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import MaleAvatar from "../img/male.jpeg";
import FemaleAvatar from "../img/female.jpeg";
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import CardActionArea from "@mui/material/CardActionArea";
import { collection, doc, getDocs, query, serverTimestamp, setDoc, where } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebaseConfig';
import LandingPageNavbar from '../components/LandingPageNavbar.js';
import landingBackground from '../img/LandingBackground.jpg';
import logo from '../img/logo.png';
import AdminIcon from '../img/AdminIcon.jpeg';
import StepButton from "@mui/material/StepButton";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import StraightenRoundedIcon from "@mui/icons-material/StraightenRounded";
import FavoriteBorderRoundedIcon from "@mui/icons-material/FavoriteBorderRounded";
import RestaurantMenuRoundedIcon from "@mui/icons-material/RestaurantMenuRounded";
import FitnessCenterRoundedIcon from "@mui/icons-material/FitnessCenterRounded";
import { useTranslation } from "react-i18next";

const COLORS = {
  primary: '#0ea5e9',
  primaryDark: '#075985',
  dark: '#0b1728',
  surface: '#0f1f33',
  success: '#22c55e',
  light: '#f8fafc',
  border: '#e2e8f0'
};

const SAMPLE_VIDEO =
  'https://www.youtube.com/watch?v=Yn-cP3vH-zU&pp=ygUMbnV0cml0aW9uaXN0';

const PARENT_HOST =
  typeof window !== 'undefined' ? window.location.hostname : 'localhost';

function toEmbedUrl(rawUrl) {
  if (!rawUrl) return '';
  const url = rawUrl.trim();

  const yt = url.match(
    /(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]{6,})/
  );
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;

  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;

  const mega = url.match(
    /^https?:\/\/mega\.nz\/file\/([A-Za-z0-9_-]+)#([A-Za-z0-9_-]+)/i
  );
  if (mega) return `https://mega.nz/embed/${mega[1]}#${mega[2]}`;

  const twitchVod = url.match(/twitch\.tv\/videos\/(\d+)/);
  if (twitchVod)
    return `https://player.twitch.tv/?video=${twitchVod[1]}&parent=${PARENT_HOST}`;

  if (/\.(mp4|webm|ogg)(\?|#|$)/i.test(url)) return url;

  return url;
}

const testimonials = [
  {
    id: 't1',
    name: 'Wejdene, 19 ans',
    before: 'wejdene-before.jpeg',
    after: 'wejdene-after.jpeg'
  },
  {
    id: 't2',
    name: 'Abdelghafer, 36 ans',
    before: 'abdelghafer-before.jpeg',
    after: 'abdelghafer-after.jpeg'
  },
  {
    id: 't3',
    name: 'Safaa, 28 ans',
    combined: 'safaa-before-after.jpeg'
  },
  {
    id: 't4',
    name: 'Wassim, 9 ans',
    combined: 'wassim-before-after.jpeg'
  },
  {
    id: 't5',
    name: 'Fatna, 51 ans',
    combined: 'fatna-before-after.jpeg'
  }
];

const CLIENT_REVIEWS_BASE = `${process.env.PUBLIC_URL || ''}/img/reviews/clients`;
const WRITTEN_REVIEWS_BASE = `${process.env.PUBLIC_URL || ''}/img/reviews/written`;
const CLIENT_PROOF_VIDEO = `${WRITTEN_REVIEWS_BASE}/review-video.mp4`;

const WRITTEN_REVIEWS = Array.from({ length: 9 }, (_, index) => ({
  id: `wr-${index + 1}`,
  src: `${WRITTEN_REVIEWS_BASE}/review-${String(index + 1).padStart(2, '0')}.jpeg`
}));

function getClientCardMd(index, total) {
  const remainder = total % 3;
  if (remainder === 1 && index === total - 1) return 12;
  if (remainder === 2 && index >= total - 2) return 6;
  return 4;
}

const RECIPE_CARDS = [
  {
    id: 'recipe-1',
    title: 'Sprout Salad with Boiled Eggs',
    description:
      'Search 2 million recipes using keywords, 28 nutrients and 40 diet and health filters',
    minutes: '20 mins',
    difficulty: 'Easy',
    servings: '2 persons',
    image: "https://i.pinimg.com/736x/68/6a/fa/686afae3df2f2d5037bfd6b58526d788.jpg"
  },
  {
    id: 'recipe-2',
    title: 'Chicken Curry',
    description:
      'Search 2 million recipes using keywords, 28 nutrients and 40 diet and health filters',
    minutes: '20 mins',
    difficulty: 'Easy',
    servings: '2 persons',
    image: "https://i.pinimg.com/736x/d2/45/40/d245409699404d97d1160983d006bcad.jpg"
  },
  {
    id: 'recipe-3',
    title: 'Vegetable Salad',
    description:
      'Search 2 million recipes using keywords, 28 nutrients and 40 diet and health filters',
    minutes: '20 mins',
    difficulty: 'Easy',
    servings: '2 persons',
    image: "https://i.pinimg.com/1200x/e5/18/dd/e518dd767463e2e5234ee2393695a225.jpg"
  }
];

const VIDEO_STATS = [
  { id: 'stat-1', value: '95%', label: 'Complete customer satisfaction' },
  { id: 'stat-2', value: '10+', label: 'Innovative receipes' },
  { id: 'stat-3', value: '10kg', label: 'Average weight loss per client per month' },
  { id: 'stat-4', value: '500', label: 'Total clients worldwide' }
];

const SHOW_RECIPES_SECTION = false;

const PLAN_DURATIONS = [
  { id: '1m', label: 'Plan 1 mois' },
  { id: '3m', label: 'Plan 3 mois' }
];

const EUROPE_COUNTRIES = new Set([
  "france", "belgium", "germany", "spain", "italy", "netherlands", "portugal",
  "switzerland", "austria", "sweden", "norway", "denmark", "finland", "ireland",
  "poland", "greece", "romania", "bulgaria", "czechia", "hungary", "croatia",
  "serbia", "bosnia and herzegovina", "slovakia", "slovenia", "albania",
  "north macedonia", "montenegro", "moldova", "estonia", "latvia", "lithuania",
  "luxembourg", "malta", "iceland", "united kingdom",
]);

function resolveRegionKeyFromCountryName(countryName) {
  const normalized = String(countryName || "").trim().toLowerCase();
  if (!normalized) return "rest_world";
  if (["algeria", "algérie", "algerie", "الجزائر", "dz"].includes(normalized)) return "algeria";
  if (["morocco", "maroc", "المغرب", "tunisia", "tunisie", "تونس", "ma", "tn"].includes(normalized)) {
    return "north_africa";
  }
  if (normalized === "turkey" || normalized === "türkiye" || normalized === "turkiye" || normalized === "tr") {
    return "turkey";
  }
  if (EUROPE_COUNTRIES.has(normalized)) return "europe";
  return "rest_world";
}

function normalizeRegionKey(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return "";
  const aliases = {
    "rest of the world": "rest_world",
    "rest of world": "rest_world",
    "rest-world": "rest_world",
    "north africa": "north_africa",
    "north-africa": "north_africa",
    "türkiye": "turkey",
    "turkiye": "turkey",
    "tr": "turkey",
  };
  return aliases[raw] || raw;
}

function normalizeCurrencyKey(value) {
  const raw = String(value || "").trim().toUpperCase();
  if (raw === "₺") return "TRY";
  if (raw === "$") return "USD";
  if (raw === "€") return "EUR";
  return raw;
}

function inferCountryFromClientContext() {
  try {
    const localeCandidates = [navigator.language, ...(navigator.languages || [])]
      .filter(Boolean)
      .map((value) => String(value).toUpperCase());
    const localeJoined = localeCandidates.join(" ");
    if (localeJoined.includes("-DZ") || localeJoined.includes("_DZ")) return "Algeria";
    if (localeJoined.includes("-MA") || localeJoined.includes("_MA")) return "Morocco";
    if (localeJoined.includes("-TN") || localeJoined.includes("_TN")) return "Tunisia";
    if (localeJoined.includes("-TR") || localeJoined.includes("_TR")) return "Turkey";

    const timezone = String(Intl.DateTimeFormat().resolvedOptions().timeZone || "").toLowerCase();
    if (timezone.includes("algiers")) return "Algeria";
    if (timezone.includes("casablanca")) return "Morocco";
    if (timezone.includes("tunis")) return "Tunisia";
    if (timezone.includes("istanbul")) return "Turkey";
  } catch (error) {
    console.warn("Unable to infer country from client context", error);
  }
  return "";
}

function resolveApiBaseUrl() {
  return String(
    process.env.REACT_APP_API_BASE_URL ||
    "https://us-central1-houari-boumedienne-schoo-4d4c2.cloudfunctions.net",
  ).replace(/\/+$/, "");
}

const PLAN_GOALS = [
  { id: 'perte', label: 'Perte de poids', accent: COLORS.primary },
  { id: 'gain', label: 'Prise de poids', accent: '#0d9488' },
  { id: 'medical', label: 'Accompagnement médical', accent: '#0b8ec2' }
];

const PROGRAM_ID = 'beHealthySamah';
const TEACHER_EMAIL = 'belagrasamah@gmail.com';
const TEACHER_UID_FALLBACK = 'f3dZHTUxTmMv0VwRvriqcOUO4xK2';

const INITIAL_ANSWERS = {
  firstName: '',
  lastName: '',
  fullName: '',
  age: '',
  gender: '',
  weightKg: '',
  heightCm: '',
  neckCm: '',
  waistCm: '',
  armCm: '',
  chestCm: '',
  hipCm: '',
  thighCm: '',
  calfCm: '',
  wilayaCountry: '',
  phoneCountryCode: '+213',
  phone: '',
  email: '',
  maritalStatus: '',
  job: '',
  monthlyIncome: '',
  goal: '',
  hasHealthIssues: '',
  healthIssues: '',
  smokingStatus: '',
  inbodyDone: '',
  inbodyFileUrl: '',
  recentTestsDone: '',
  recentTestsFileUrl: '',
  hormones: '',
  hormonesDetails: '',
  medicationsTaking: '',
  medications: '',
  supplementsTaking: '',
  supplements: '',
  yesterdayMeals: '',
  dietHistory: '',
  favoriteFoods: '',
  dislikedOrAllergyFoods: '',
  hasKitchenScale: '',
  sportNow: '',
  sportSessionsPerWeek: '',
  sportFollowUpInterest: '',
  sportLocation: '',
  sportSchedule: '',
  bodyPhotoUrl: ''
};

const FORM_SECTIONS = [
  {
    id: 'identite',
    title: 'الهوية والتواصل',
    fields: [
      { key: 'firstName', label: 'الاسم', required: true },
      { key: 'lastName', label: 'اللقب', required: true },
      { key: 'age', label: 'العمر', required: true, type: 'number' },
      {
        key: 'gender',
        label: 'الجنس',
        required: true,
        type: 'radio',
        options: [
          { value: 'male', label: 'ذكر' },
          { value: 'female', label: 'أنثى' }
        ]
      },
      { key: 'wilayaCountry', label: 'الولاية / البلد', required: true },
      {
        key: 'phoneCountryCode',
        label: 'رمز الدولة',
        required: true,
        type: 'select',
        options: [
          { value: '+213', label: 'الجزائر (+213)', flag: '🇩🇿' },
          { value: '+212', label: 'المغرب (+212)', flag: '🇲🇦' },
          { value: '+216', label: 'تونس (+216)', flag: '🇹🇳' },
          { value: '+20', label: 'مصر (+20)', flag: '🇪🇬' },
          { value: '+966', label: 'السعودية (+966)', flag: '🇸🇦' },
          { value: '+971', label: 'الإمارات (+971)', flag: '🇦🇪' },
          { value: '+974', label: 'قطر (+974)', flag: '🇶🇦' },
          { value: '+968', label: 'عُمان (+968)', flag: '🇴🇲' },
          { value: '+961', label: 'لبنان (+961)', flag: '🇱🇧' },
          { value: '+962', label: 'الأردن (+962)', flag: '🇯🇴' },
          { value: '+964', label: 'العراق (+964)', flag: '🇮🇶' },
          { value: '+970', label: 'فلسطين (+970)', flag: '🇵🇸' },
          { value: '+90', label: 'Türkiye (+90)', flag: '🇹🇷' },
          { value: '+93', label: 'Afghanistan (+93)', flag: '🇦🇫' },
          { value: '+1', label: 'USA / Canada (+1)', flag: '🇺🇸' },
          { value: '+44', label: 'UK (+44)', flag: '🇬🇧' },
          { value: '+33', label: 'France (+33)', flag: '🇫🇷' },
          { value: '+34', label: 'España (+34)', flag: '🇪🇸' },
          { value: '+39', label: 'Italia (+39)', flag: '🇮🇹' },
          { value: '+49', label: 'Deutschland (+49)', flag: '🇩🇪' },
          { value: '+41', label: 'Suisse (+41)', flag: '🇨🇭' },
          { value: '+32', label: 'Belgique (+32)', flag: '🇧🇪' },
          { value: '+31', label: 'Nederland (+31)', flag: '🇳🇱' },
          { value: '+352', label: 'Luxembourg (+352)', flag: '🇱🇺' },
          { value: '+46', label: 'Sverige (+46)', flag: '🇸🇪' },
          { value: '+47', label: 'Norge (+47)', flag: '🇳🇴' },
          { value: '+48', label: 'Polska (+48)', flag: '🇵🇱' },
          { value: '+351', label: 'Portugal (+351)', flag: '🇵🇹' },
          { value: '+30', label: 'Ελλάδα (+30)', flag: '🇬🇷' }
        ]
      },
      { key: 'phone', label: 'رقم الهاتف', required: true },
      { key: 'email', label: 'البريد الإلكتروني', required: true },
      {
        key: 'maritalStatus',
        label: 'الحالة الاجتماعية',
        required: true,
        type: 'select',
        options: [
          { value: 'single', label: 'أعزب' },
          { value: 'married', label: 'متزوج' },
          { value: 'mother', label: 'أم' },
          { value: 'breastfeeding', label: 'مرضعة' },
          { value: 'pregnant', label: 'حامل' }
        ]
      },
      { key: 'job', label: 'المهنة', required: true },
      { key: 'monthlyIncome', label: 'متوسط الدخل في الشهر' }
    ]
  },
  {
    id: 'mesures',
    title: 'القياسات',
    fields: [
      { key: 'weightKg', label: 'الوزن (كغ)', required: true, type: 'number' },
      { key: 'heightCm', label: 'الطول (سم)', required: true, type: 'number' },
      { key: 'neckCm', label: 'محيط الرقبة (سم)', type: 'number' },
      { key: 'waistCm', label: 'مقاس محيط البطن (سم)', type: 'number' },
      { key: 'armCm', label: 'مقاس الذراعين (سم)', type: 'number' },
      { key: 'chestCm', label: 'مقاس الصدر (سم)', type: 'number' },
      { key: 'hipCm', label: 'مقاس الأرداف (سم)', type: 'number' },
      { key: 'thighCm', label: 'مقاس الفخذين (سم)', type: 'number' },
      { key: 'calfCm', label: 'محيط عضلة الساق (سم)', type: 'number' }
    ]
  },
  {
    id: 'sante',
    title: 'الصحة والتحاليل',
    fields: [
      { key: 'goal', label: 'الهدف من البرنامج', required: true },
      {
        key: 'hasHealthIssues',
        label: 'هل تعاني من أي مشكل صحي؟',
        required: true,
        type: 'radio',
        options: [
          { value: 'yes', label: 'نعم' },
          { value: 'no', label: 'لا' }
        ]
      },
      {
        key: 'healthIssues',
        label: 'مشاكل صحية (إن وجدت)',
        requiredIf: (answers) => answers.hasHealthIssues === 'yes'
      },
      {
        key: 'smokingStatus',
        label: 'هل أنت مدخن؟',
        required: true,
        type: 'select',
        options: [
          { value: 'yes', label: 'نعم' },
          { value: 'no', label: 'لا' },
          { value: 'stopped', label: 'توقفت عن التدخين' }
        ]
      },
      {
        key: 'inbodyDone',
        label: 'هل قمت بتحليل Inbody؟',
        required: true,
        type: 'radio',
        options: [
          { value: 'yes', label: 'نعم' },
          { value: 'no', label: 'لا' }
        ]
      },
      {
        key: 'inbodyFileUrl',
        label: 'تحميل نتيجة Inbody (اختياري إذا نعم)',
        type: 'file',
        requiredIf: (answers) => answers.inbodyDone === 'yes'
      },
      {
        key: 'recentTestsDone',
        label: 'تحاليل حديثة (آخر 6 أشهر)؟',
        required: true,
        type: 'radio',
        options: [
          { value: 'yes', label: 'نعم' },
          { value: 'no', label: 'لا' }
        ]
      },
      {
        key: 'recentTestsFileUrl',
        label: 'تحميل نتائج التحاليل (اختياري إذا نعم)',
        type: 'file',
        requiredIf: (answers) => answers.recentTestsDone === 'yes'
      },
      {
        key: 'hormones',
        label: 'هل تأخذ هرمونات أو أخذت من قبل؟',
        required: true,
        type: 'radio',
        options: [
          { value: 'yes', label: 'نعم' },
          { value: 'no', label: 'لا' }
        ]
      },
      {
        key: 'hormonesDetails',
        label: 'ما هي الهرمونات؟ (إن وجدت)',
        requiredIf: (answers) => answers.hormones === 'yes'
      },
      {
        key: 'medicationsTaking',
        label: 'هل تأخذ أدوية؟',
        required: true,
        type: 'radio',
        options: [
          { value: 'yes', label: 'نعم' },
          { value: 'no', label: 'لا' }
        ]
      },
      {
        key: 'medications',
        label: 'الأدوية (إن وجدت)',
        requiredIf: (answers) => answers.medicationsTaking === 'yes'
      },
      {
        key: 'supplementsTaking',
        label: 'هل تأخذ مكملات غذائية؟',
        required: true,
        type: 'radio',
        options: [
          { value: 'yes', label: 'نعم' },
          { value: 'no', label: 'لا' }
        ]
      },
      {
        key: 'supplements',
        label: 'المكملات الغذائية (إن وجدت)',
        requiredIf: (answers) => answers.supplementsTaking === 'yes'
      }
    ]
  },
  {
    id: 'nutrition',
    title: 'الغذاء والتفضيلات',
    fields: [
      { key: 'yesterdayMeals', label: 'ماذا أكلت بالأمس بالتفصيل؟', required: true },
      {
        key: 'dietHistory',
        label:
          'هل اتبعت نظام غذائي من قبل؟ هل كان مريح/قاسي؟ النتائج خلال شهر؟',
        required: true
      },
      { key: 'favoriteFoods', label: 'أكثر الأكلات/الأطباق التي تحبها', required: true },
      {
        key: 'dislikedOrAllergyFoods',
        label: 'أكلات لا تحبها أو لديك حساسية منها',
        required: true
      },
      {
        key: 'hasKitchenScale',
        label: 'هل عندك ميزان في المطبخ؟',
        required: true,
        type: 'select',
        options: [
          { value: 'yes', label: 'نعم' },
          { value: 'no', label: 'لا' },
          { value: 'will_buy', label: 'سأشتري قبل البدء' }
        ]
      }
    ]
  },
  {
    id: 'sport',
    title: 'النشاط البدني والمتابعة الرياضية',
    fields: [
      {
        key: 'sportNow',
        label: 'هل تمارس رياضة حاليا؟ ما نوعها؟ منذ متى؟',
        required: true
      },
      {
        key: 'sportSessionsPerWeek',
        label: 'كم حصة رياضة بالأسبوع حالياً؟',
        required: true,
        type: 'number'
      },
      {
        key: 'sportFollowUpInterest',
        label: 'هل أنت مهتم بمتابعة رياضية إلى جانب التغذية؟',
        required: true,
        type: 'radio',
        options: [
          { value: 'yes', label: 'نعم' },
          { value: 'no', label: 'لا' }
        ]
      },
      {
        key: 'sportLocation',
        label: 'مكان التمرين (إذا مهتم بالمتابعة الرياضية)',
        type: 'select',
        options: [
          { value: 'home', label: 'البيت' },
          { value: 'gym', label: 'الجيم' },
          { value: 'both', label: 'كلاهما' }
        ],
        requiredIf: (answers) => answers.sportFollowUpInterest === 'yes'
      },
      {
        key: 'sportSchedule',
        label: 'حدد الأيام والتوقيت للتمرين',
        requiredIf: (answers) => answers.sportFollowUpInterest === 'yes'
      },
      {
        key: 'bodyPhotoUrl',
        label: 'صورة شكل الجسم (إذا متابعة رياضية)',
        type: 'file',
        requiredIf: (answers) => answers.sportFollowUpInterest === 'yes'
      }
    ]
  }
];

const PLAN_CATALOG = {
  perte: {
    '1m': {
      title: 'Perte de poids — 1 mois',
      subtitle: 'Démarrage express',
      accent: COLORS.primary,
      description:
        'Rééquilibrage rapide, contrôle des fringales et routine simple pour lancer la perte.',
      features: [
        'Bilan initial + objectifs clairs',
        'Plan alimentaire hebdomadaire personnalisé',
        'Ajustements chaque semaine (chat)',
        'Routine hydratation + mouvements doux'
      ]
    },
    '3m': {
      title: 'Perte de poids — 3 mois',
      subtitle: 'Transformation durable',
      accent: COLORS.primary,
      description:
        'Stabilisation progressive, gestion des cravings et plan évolutif toutes les 2 semaines.',
      features: [
        'Bilan complet + suivi biomarqueurs (si dispo)',
        'Plans évolutifs toutes les 2 semaines',
        'Coaching mindset & gestion des cravings',
        'Check vidéo mensuel + messages illimités'
      ]
    }
  },
  gain: {
    '1m': {
      title: 'Prise de poids — 1 mois',
      subtitle: 'Relance musculaire',
      accent: '#0d9488',
      description:
        'Augmentation progressive des apports, densité nutritionnelle et suivi digestif.',
      features: [
        'Bilan initial + macros sur mesure',
        'Plan hypercalorique équilibré',
        'Suivi hebdo + ajustements portion',
        'Routine force & récupération'
      ]
    },
    '3m': {
      title: 'Prise de poids — 3 mois',
      subtitle: 'Construction solide',
      accent: '#0d9488',
      description:
        'Progression contrôlée, travail musculaire et optimisation digestion/sommeil.',
      features: [
        'Plans évolutifs toutes les 2 semaines',
        'Macros adaptées à l’entraînement',
        'Suivi digestif + amélioration sommeil',
        'Check vidéo mensuel + messages illimités'
      ]
    }
  },
  medical: {
    '1m': {
      title: 'Accompagnement médical — 1 mois',
      subtitle: 'Stabilisation',
      accent: '#0b8ec2',
      description:
        'Plans validés avec votre médecin (SOPK, diabète, HTA…) et démarrage en douceur.',
      features: [
        'Bilan + points médicaux partagés',
        'Plan alimentaire adapté aux contraintes',
        'Suivi hebdo + messages illimités',
        'Ajustements prudents et documentés'
      ]
    },
    '3m': {
      title: 'Accompagnement médical — 3 mois',
      subtitle: 'Sécurisé & progressif',
      accent: '#0b8ec2',
      description:
        'Suivi rapproché avec validations régulières et plans sécurisés sur la durée.',
      features: [
        'Plan évolutif toutes les 2 semaines',
        'Coordination médicale (au besoin)',
        'Coaching mindset + gestion stress',
        'Check vidéo mensuel + messages illimités'
      ]
    }
  }
};

function IntakeFormDialog({ open, onClose, planMeta, teacherUid, onSubmitSuccess }) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir(i18n.resolvedLanguage || i18n.language) === "rtl";
  const fieldDirection = isRtl ? "rtl" : "ltr";
  const fieldTextAlign = isRtl ? "right" : "left";
  const safeLabelSx = {
    maxWidth: "calc(100% - 24px)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    textAlign: fieldTextAlign,
    direction: fieldDirection,
  };
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState(INITIAL_ANSWERS);
  const [files, setFiles] = useState({
    inbodyFileUrl: null,
    recentTestsFileUrl: null,
    bodyPhotoUrl: null
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [missingFieldKeys, setMissingFieldKeys] = useState([]);
  const [selectedGoalId, setSelectedGoalId] = useState(planMeta?.goalId || "perte");
  const [selectedDurationId, setSelectedDurationId] = useState(planMeta?.durationId || "3m");
  const isServiceCheckoutFlow = Boolean(planMeta?.serviceId);
  const dialogContentRef = useRef(null);

  // Parent can close the dialog after async submit success; reset on close to
  // guarantee a fresh form state on the next open.
  useEffect(() => {
    if (open) return;
    setStep(0);
    setAnswers(INITIAL_ANSWERS);
    setFiles({ inbodyFileUrl: null, recentTestsFileUrl: null, bodyPhotoUrl: null });
    setError('');
    setSuccess('');
    setMissingFieldKeys([]);
  }, [open]);

  // Keep dropdowns synced when the dialog opens or planMeta changes
  useEffect(() => {
    if (!open) return;
    setSelectedGoalId(planMeta?.goalId || "perte");
    setSelectedDurationId(planMeta?.durationId || "3m");
  }, [open, planMeta?.goalId, planMeta?.durationId]);

  // Current plan meta computed from dropdown selection
  const selectedPlanMeta = useMemo(() => {
    const plan =
      PLAN_CATALOG[selectedGoalId]?.[selectedDurationId] ||
      PLAN_CATALOG["perte"]?.["3m"];

    return {
      planId: `${selectedGoalId}-${selectedDurationId}`,
      goalId: selectedGoalId,
      durationId: selectedDurationId,
      title: plan?.title || "",
    };
  }, [selectedGoalId, selectedDurationId]);


  const fileKeyMap = {
    inbodyFileUrl: 'inbody',
    recentTestsFileUrl: 'recent-tests',
    bodyPhotoUrl: 'body-photo'
  };

  const numberKeys = [
    'age',
    'weightKg',
    'heightCm',
    'neckCm',
    'waistCm',
    'armCm',
    'chestCm',
    'hipCm',
    'thighCm',
    'calfCm',
    'sportSessionsPerWeek'
  ];

  const STEP_META = [
    { id: "identite", label: "الهوية والتواصل", icon: <PersonOutlineRoundedIcon /> },
    { id: "mesures", label: "القياسات", icon: <StraightenRoundedIcon /> },
    { id: "sante", label: "الصحة والتحاليل", icon: <FavoriteBorderRoundedIcon /> },
    { id: "nutrition", label: "الغذاء والتفضيلات", icon: <RestaurantMenuRoundedIcon /> },
    { id: "sport", label: "النشاط والمتابعة", icon: <FitnessCenterRoundedIcon /> },
  ];


  const resetState = () => {
    setStep(0);
    setAnswers(INITIAL_ANSWERS);
    setFiles({ inbodyFileUrl: null, recentTestsFileUrl: null, bodyPhotoUrl: null });
    setError('');
    setSuccess('');
    setMissingFieldKeys([]);
  };

  const handleClose = () => {
    resetState();
    onClose?.();
  };

  const toNumberOrNull = (value) => {
    if (value === '' || value === null || value === undefined) return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };

  const handleFieldChange = (key, value) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
    setMissingFieldKeys((prev) => prev.filter((fieldKey) => fieldKey !== key));
  };

  const handleFileChange = (key, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setError(t('حجم الملف يجب أن يكون أقل من 10 ميغابايت.'));
      return;
    }
    setFiles((prev) => ({ ...prev, [key]: file }));
    setMissingFieldKeys((prev) => prev.filter((fieldKey) => fieldKey !== key));
  };

  const getMissingRequiredFields = (section) => {
    const missing = [];
    section.fields.forEach((field) => {
      const isRequired =
        field.required ||
        (typeof field.requiredIf === 'function' && field.requiredIf(answers));

      if (!isRequired) return;

      if (field.type === 'file') {
        const fileObj = files[field.key];
        const urlValue = answers[field.key];
        if (!fileObj && !urlValue) missing.push(field);
        return;
      }

      const value = answers[field.key];
      const isEmpty = value === '' || value === null || value === undefined;
      if (isEmpty) missing.push(field);
    });
    return missing;
  };

  const requiredMissingFields = (section) => getMissingRequiredFields(section).map((field) => t(field.label));

  const scrollDialogToTop = () => {
    const contentNode = dialogContentRef.current;
    if (!contentNode) return;
    if (typeof contentNode.scrollTo === "function") {
      contentNode.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    contentNode.scrollTop = 0;
  };

  useEffect(() => {
    if (!error) return;
    const frame = requestAnimationFrame(() => {
      scrollDialogToTop();
    });
    return () => cancelAnimationFrame(frame);
  }, [error]);

  const uploadFileIfNeeded = async (file, intakeId, fileKey) => {
    if (!file) return '';
    const storageRef = ref(
      storage,
      `programIntakes/${PROGRAM_ID}/${intakeId}/${fileKey}-${Date.now()}-${file.name}`
    );
    const snapshot = await uploadBytes(storageRef, file);
    return getDownloadURL(snapshot.ref);
  };

  const handleSubmit = async () => {
    setError('');
    setSuccess('');

    if (!teacherUid) {
      setError(t("Impossible d'identifier l'enseignante. Veuillez réessayer plus tard."));
      return;
    }

    // Validate all sections before submit
    const allMissing = FORM_SECTIONS.flatMap((section) => requiredMissingFields(section));
    if (allMissing.length) {
      setError(t('يرجى تعبئة الحقول الإلزامية: {{fields}}', { fields: allMissing.join(', ') }));
      return;
    }

    setSubmitting(true);
    try {
      const selectedRegionKey = normalizeRegionKey(planMeta?.matchedPrice?.regionKey);
      const isDeferredStripeRegistrationFlow =
        isServiceCheckoutFlow && selectedRegionKey !== "algeria";
      const intakesRef = collection(
        db,
        isDeferredStripeRegistrationFlow ? "programIntakesPending" : "programIntakes"
      );
      const intakeRef = doc(intakesRef);
      const intakeId = intakeRef.id;
      const computedFullName = `${(answers.firstName || '').trim()} ${(answers.lastName || '').trim()}`.trim();
      const fullPhone = `${(answers.phoneCountryCode || '').trim()}${(answers.phone || '').trim()}`.trim();
      const leadEmail = (answers.email || '').trim().toLowerCase();

      // Upload files (if any)
      const [inbodyUrl, recentUrl, bodyPhotoUrl] = await Promise.all([
        uploadFileIfNeeded(files.inbodyFileUrl, intakeId, fileKeyMap.inbodyFileUrl),
        uploadFileIfNeeded(files.recentTestsFileUrl, intakeId, fileKeyMap.recentTestsFileUrl),
        uploadFileIfNeeded(files.bodyPhotoUrl, intakeId, fileKeyMap.bodyPhotoUrl)
      ]);

      const normalizedAnswers = { ...answers };
      numberKeys.forEach((key) => {
        normalizedAnswers[key] = toNumberOrNull(answers[key]);
      });

      // Override URLs with uploaded ones
      normalizedAnswers.inbodyFileUrl = inbodyUrl || answers.inbodyFileUrl || '';
      normalizedAnswers.recentTestsFileUrl = recentUrl || answers.recentTestsFileUrl || '';
      normalizedAnswers.bodyPhotoUrl = bodyPhotoUrl || answers.bodyPhotoUrl || '';
      normalizedAnswers.fullName = computedFullName || answers.fullName || '';

      const payload = {
        programId: PROGRAM_ID,
        teacherUid,
        studentEmail: leadEmail || null,
        intakeVersion: 1,
        selectedPlan: selectedPlanMeta.planId || null,
        goalId: selectedPlanMeta.goalId || null,
        durationId: selectedPlanMeta.durationId || null,
        selectedServiceId: planMeta?.serviceId || null,
        selectedServiceName: planMeta?.serviceName || null,
        selectedServiceRegionKey: planMeta?.matchedPrice?.regionKey || null,
        selectedServiceCurrency: planMeta?.matchedPrice?.currency || null,
        selectedServiceAmount: Number(planMeta?.matchedPrice?.amount || 0) || null,
        status: isDeferredStripeRegistrationFlow ? "pending_payment" : "submitted",
        paymentStatus: isDeferredStripeRegistrationFlow ? "awaiting_checkout" : "pending",
        checkoutSessionId: null,
        studentId: null,
        leadEmail: leadEmail || null,
        leadPhone: fullPhone || null,
        clientCountry: (answers.wilayaCountry || "").trim() || null,
        deferredStripeRegistration: isDeferredStripeRegistrationFlow,
        fullName: computedFullName || answers.fullName || null,
        firstName: (answers.firstName || '').trim() || null,
        lastName: (answers.lastName || '').trim() || null,
        answers: normalizedAnswers,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await setDoc(intakeRef, payload);

      if (leadEmail && !isDeferredStripeRegistrationFlow) {
        const studentsRef = collection(db, 'students');
        const studentQuery = query(studentsRef, where('email', '==', leadEmail));
        const studentSnap = await getDocs(studentQuery);
        if (studentSnap.empty) {
          const studentRef = doc(studentsRef);
          await setDoc(studentRef, {
            firstName: (answers.firstName || '').trim() || null,
            lastName: (answers.lastName || '').trim() || null,
            fullName: computedFullName || null,
            email: leadEmail,
            phone: (answers.phone || '').trim() || null,
            adminValidated: false,
            teacherUid,
            latestIntakeId: intakeId,
            entryDate: new Date().toISOString(),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        } else {
          const studentRef = doc(db, 'students', studentSnap.docs[0].id);
          await setDoc(
            studentRef,
            {
              email: leadEmail,
              teacherUid,
              adminValidated: false,
              latestIntakeId: intakeId,
              updatedAt: serverTimestamp()
            },
            { merge: true }
          );
        }
      }

      if (isServiceCheckoutFlow && typeof onSubmitSuccess === "function") {
        await onSubmitSuccess({
          intakeId,
          selectedPlanMeta,
          serviceId: planMeta?.serviceId || null,
        });
        return;
      }

      setSuccess(t('تم إرسال النموذج بنجاح. سنقوم بالرد عليك قريبًا.'));
    } catch (err) {
      console.error('Failed to submit intake form', err);
      setError(err?.message || t('حدث خطأ أثناء حفظ النموذج. يرجى المحاولة مرة أخرى.'));
    } finally {
      setSubmitting(false);
    }
  };

  const goToStep = (targetIndex) => {
    if (submitting) return;

    // Always allow going backwards
    if (targetIndex <= step) {
      setError("");
      setMissingFieldKeys([]);
      setStep(targetIndex);
      return;
    }

    // If going forward, validate the CURRENT step first
    const missing = getMissingRequiredFields(FORM_SECTIONS[step]);
    if (missing.length) {
      setMissingFieldKeys(missing.map((field) => field.key));
      setError(t('يرجى تعبئة الحقول الإلزامية في هذه الخطوة: {{fields}}', { fields: missing.map((field) => t(field.label)).join(', ') }));
      return;
    }

    setError("");
    setMissingFieldKeys([]);
    setStep(targetIndex);
  };

  const handleNext = () => {
    const missing = getMissingRequiredFields(FORM_SECTIONS[step]);
    if (missing.length) {
      setMissingFieldKeys(missing.map((field) => field.key));
      setError(t('يرجى تعبئة الحقول الإلزامية في هذه الخطوة: {{fields}}', { fields: missing.map((field) => t(field.label)).join(', ') }));
      return;
    }
    setError('');
    setMissingFieldKeys([]);
    setStep((prev) => Math.min(prev + 1, FORM_SECTIONS.length - 1));
  };

  const handleBack = () => {
    setError('');
    setMissingFieldKeys([]);
    setStep((prev) => Math.max(prev - 1, 0));
  };

  const renderField = (field) => {
    const value = answers[field.key];
    const isRequired =
      field.required || (typeof field.requiredIf === 'function' && field.requiredIf(answers));
    const label = t(field.label);
    const isMissing = missingFieldKeys.includes(field.key);

    if (field.type === "radio") {
      // Special UI: Gender as selectable cards
      if (field.key === "gender") {
        const options = [
          { value: "male", label: t("ذكر"), image: MaleAvatar },
          { value: "female", label: t("أنثى"), image: FemaleAvatar },
        ];

        return (
          <Card
            elevation={0}
            key={field.key}
            sx={{
              bgcolor: "#fff",
              borderRadius: 3,
              border: isMissing ? "1px solid #ef4444" : "1px solid transparent",
              p: isMissing ? 1.2 : 0,
            }}
            dir={fieldDirection}
          >
            <Grid container spacing={2}>
              {options.map((opt) => {
                const selected = value === opt.value;

                return (
                  <Grid item xs={6} key={opt.value}>
                    <Card
                      elevation={0}
                      sx={{
                        borderRadius: 3,
                        border: selected
                          ? `2px solid ${COLORS.primary}`
                          : `1px solid ${isMissing ? "#ef4444" : COLORS.border}`,
                        bgcolor: selected ? "rgba(14,165,233,0.06)" : "#fff",
                        transition: "0.15s ease",
                        boxShadow: selected ? "0 14px 30px rgba(14,165,233,0.16)" : "none",
                      }}
                    >
                      <CardActionArea
                        onClick={() => handleFieldChange(field.key, opt.value)}
                        sx={{
                          borderRadius: 3,
                          p: 2,
                          textAlign: "center",
                        }}
                      >
                        <Box
                          sx={{
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                            mb: 1.4,
                          }}
                        >
                          <Box
                            component="img"
                            src={opt.image}
                            alt={opt.label}
                            sx={{
                              width: 74,
                              height: 74,
                              objectFit: "contain",
                              filter: selected ? "none" : "grayscale(10%)",
                            }}
                          />
                        </Box>

                        <Typography
                          sx={{
                            fontWeight: 900,
                            fontSize: 16,
                            color: selected ? "#0b1728" : "#0f172a",
                          }}
                        >
                          {opt.label}
                        </Typography>

                        {/* Hidden input semantics (optional but clean) */}
                        <input
                          type="radio"
                          name={field.key}
                          value={opt.value}
                          checked={selected}
                          readOnly
                          style={{ display: "none" }}
                        />
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
            {isMissing && (
              <FormHelperText error sx={{ mt: 1, textAlign: fieldTextAlign }}>
                {t("هذا الحقل مطلوب")}
              </FormHelperText>
            )}
          </Card>
        );
      }

      // Default radio rendering for other fields (unchanged behavior)
      return (
        <Card
          elevation={0}
          key={field.key}
          sx={{
            borderRadius: 3,
            border: `1px solid ${isMissing ? "#ef4444" : COLORS.border}`,
            bgcolor: "#fff",
            p: 2,
          }}
          dir={fieldDirection}
        >
          <Typography sx={{ fontWeight: 900, color: "#0b1728", mb: 1 }}>
            {label} {isRequired ? "*" : ""}
          </Typography>

          <RadioGroup
            row
            value={value}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            sx={{
              gap: 2,
              "& .MuiFormControlLabel-label": { fontWeight: 700, color: "#0f172a" },
            }}
          >
            {field.options?.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio sx={{ "&.Mui-checked": { color: COLORS.primary } }} />}
                label={t(option.label)}
              />
            ))}
          </RadioGroup>

          {isMissing ? (
            <FormHelperText error sx={{ mt: 0.8, textAlign: fieldTextAlign }}>
              {t("هذا الحقل مطلوب")}
            </FormHelperText>
          ) : (
            isRequired && (
              <Typography sx={{ mt: 0.8, fontSize: 12, color: "#64748b" }}>
                {t("حقل إجباري")}
              </Typography>
            )
          )}
        </Card>
      );
    }

    if (field.type === "select") {
      return (
        <FormControl
          fullWidth
          key={field.key}
          error={isMissing}
          dir={fieldDirection}
          sx={{ "& .MuiInputLabel-root": safeLabelSx }}
        >
          <InputLabel sx={safeLabelSx}>{label}</InputLabel>
          <Select
            label={label}
            value={value}
            onChange={(e) => handleFieldChange(field.key, e.target.value)}
            sx={{
              borderRadius: 3,
              bgcolor: "#fff",
              direction: fieldDirection,
            }}
          >
            {field.options?.map((option) => (
              <MenuItem value={option.value} key={option.value}>
                {option.flag ? `${option.flag} ${t(option.label)}` : t(option.label)}
              </MenuItem>
            ))}
          </Select>

          {isMissing ? (
            <FormHelperText sx={{ fontWeight: 700, textAlign: fieldTextAlign }}>{t("هذا الحقل مطلوب")}</FormHelperText>
          ) : isRequired ? (
            <FormHelperText sx={{ fontWeight: 700, textAlign: fieldTextAlign }}>{t("حقل إجباري")}</FormHelperText>
          ) : (
            <FormHelperText sx={{ textAlign: fieldTextAlign }}>{t("اختياري")}</FormHelperText>
          )}
        </FormControl>
      );
    }

    if (field.type === "file") {
      const fileObj = files[field.key];

      return (
        <Card
          elevation={0}
          key={field.key}
          sx={{
            borderRadius: 3,
            border: `1px dashed ${isMissing ? "#ef4444" : fileObj ? COLORS.primary : COLORS.border}`,
            bgcolor: isMissing ? "rgba(239,68,68,0.04)" : fileObj ? "rgba(14,165,233,0.06)" : "#fff",
            p: 2,
          }}
          dir={fieldDirection}
        >
          <Typography sx={{ fontWeight: 900, color: "#0b1728", mb: 0.8 }}>
            {label} {isRequired ? "*" : ""}
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} alignItems={{ sm: "center" }}>
            <Button
              variant="contained"
              component="label"
              disabled={submitting}
              sx={{
                borderRadius: 2.5,
                fontWeight: 900,
                textTransform: "none",
                backgroundColor: COLORS.primary,
                "&:hover": { backgroundColor: COLORS.primaryDark },
                width: { xs: "100%", sm: "auto" },
              }}
            >
              {fileObj ? t("تغيير الملف") : t("تحميل ملف")}
              <input
                type="file"
                hidden
                onChange={(e) => handleFileChange(field.key, e.target.files[0])}
              />
            </Button>

            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: 13, color: fileObj ? "#0b1728" : "#64748b", fontWeight: 700, mr: 1 }}>
                {fileObj ? fileObj.name : t("10 ميغابايت كحد أقصى")}
              </Typography>
              {isMissing ? (
                <FormHelperText error sx={{ mt: 0.3, textAlign: fieldTextAlign }}>
                  {t("هذا الحقل مطلوب")}
                </FormHelperText>
              ) : isRequired && (
                <Typography sx={{ fontSize: 12, color: "#64748b", mt: 0.3 }}>
                  {t("مطلوب إذا كانت الإجابة نعم")}
                </Typography>
              )}
            </Box>
          </Stack>
        </Card>
      );
    }

    return (
      <TextField
        key={field.key}
        label={label}
        type={field.type === "number" ? "number" : "text"}
        fullWidth
        required={isRequired}
        error={isMissing}
        value={value}
        onChange={(e) => handleFieldChange(field.key, e.target.value)}
        multiline={field.type !== "number" && !field.options && label.length > 35}
        minRows={label.length > 35 ? 2 : 1}
        helperText={isMissing ? t("هذا الحقل مطلوب") : ""}
        dir={fieldDirection}
        sx={{
          // Input container
          "& .MuiOutlinedInput-root": {
            borderRadius: 3,
            bgcolor: "#fff",
          },

          // Keep text direction consistent with the active language.
          "& .MuiOutlinedInput-input": {
            direction: fieldDirection,
            textAlign: fieldTextAlign,
          },

          "& .MuiInputLabel-root": {
            fontWeight: 800,
            ...safeLabelSx,
          },
          "& .MuiFormHelperText-root": {
            textAlign: fieldTextAlign,
          },
        }}
      />
    );
  };

  const shouldRenderField = (field) => {
    if (typeof field.requiredIf !== "function") return true;
    return field.requiredIf(answers);
  };
  const isSubmitSuccess = Boolean(success);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
          border: "1px solid #eee7dc",
          boxShadow: "0 30px 90px rgba(75, 70, 63, 0.28)",
          background: "linear-gradient(180deg, #fffdf9 0%, #f7f2ea 100%)",
        },
      }}
    >
      {/* Top premium header (matches page hero vibe) */}
      <Box
        sx={{
          position: "relative",
          px: { xs: 2.5, md: 3.5 }, 
          pt: 2.5,
          pb: 2,
          color: "#fff",
          backgroundImage:
            "linear-gradient(135deg, #787d73ff 0%, #737c6aff, #6d7a61ff 100%)",
        }}
        dir={fieldDirection}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
          <Box sx={{ pr: 0.5 }}>
            <Typography sx={{ fontWeight: 900, fontSize: { xs: 18, md: 20 }, lineHeight: 1.2 }}>
              {t('استبيان قبل الدفع — {{plan}}', {
                plan: isServiceCheckoutFlow
                  ? planMeta?.title || planMeta?.serviceName || t("Service")
                  : selectedPlanMeta?.title || planMeta?.title || t('Plan'),
              })}
            </Typography>

            <Typography sx={{ mt: 0.6, color: "rgba(255,255,255,0.82)", fontSize: 13, maxWidth: 520 }}>
              {t(
                'يرجى تعبئة المعلومات بدقة لضبط برنامجك قبل الدفع. الإجابات تساعد المدربة على تخصيص الخطة.'
              )}
            </Typography>

            {/* Small meta chips */}
            <Stack direction="row" spacing={1} sx={{ mt: 1.6, flexWrap: "wrap" }}>
              <Chip
                size="small"
                label={t(FORM_SECTIONS[step]?.title || '')}
                sx={{
                  bgcolor: "rgba(255,255,255,0.10)",
                  color: "rgba(255,255,255,0.92)",
                }}
              />
            </Stack>
          </Box>

          <IconButton
            onClick={handleClose}
            sx={{
              mt: -0.5,
              color: "#fff",
              bgcolor: "rgba(255,255,255,0.10)",
              border: "1px solid rgba(255,255,255,0.18)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.16)" },
            }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Progress bar integrated under header */}
      {submitting && (
        <LinearProgress
          sx={{
            height: 3,
            backgroundColor: '#efe6db',
            '& .MuiLinearProgress-bar': { backgroundColor: '#c2a47e' }
          }}
        />
      )}

      <DialogContent
        ref={dialogContentRef}
        sx={{
          p: { xs: 2, md: 3 },
          pt: { xs: 2.5, md: 3 },
          backgroundColor: '#fffdf9'
        }}
      >
        {/* Error alert */}
        {!isSubmitSuccess && error && (
          <Box sx={{ mb: 2 }}>
            <Alert
              severity="error"
              sx={{
                borderRadius: 3,
                border: "1px solid rgba(239,68,68,0.22)",
                bgcolor: "rgba(239,68,68,0.05)",
                "& .MuiAlert-message": { width: "100%" },
              }}
            >
              {error}
            </Alert>
          </Box>
        )}

        {isSubmitSuccess && (
          <Card
            elevation={0}
            sx={{
              borderRadius: 4,
              border: "1px solid rgba(34,197,94,0.22)",
              bgcolor: "#ffffff",
              p: { xs: 2.5, md: 4 },
              textAlign: "center",
              boxShadow: "0 18px 40px rgba(34,197,94,0.12)",
            }}
            dir={fieldDirection}
          >
            <Box
              sx={{
                width: 76,
                height: 76,
                mx: "auto",
                mb: 1.6,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                bgcolor: "rgba(34,197,94,0.12)",
                color: "#16a34a",
                border: "1px solid rgba(34,197,94,0.28)",
                boxShadow: "0 10px 24px rgba(34,197,94,0.14)",
              }}
            >
              <CheckRoundedIcon sx={{ fontSize: 40 }} />
            </Box>
            <Typography sx={{ fontWeight: 900, color: "#14532d", fontSize: { xs: 22, md: 24 }, mb: 0.8 }}>
              {t("تم استلام طلبك بنجاح")}
            </Typography>
            <Typography sx={{ color: "#166534", fontWeight: 600, maxWidth: 680, mx: "auto", lineHeight: 1.8 }}>
              {success}
            </Typography>
          </Card>
        )}

        {!isSubmitSuccess && (
          <>

          <Box dir={fieldDirection} sx={{ mb: 2 }}>
            <Stepper
              nonLinear
              activeStep={step}
              alternativeLabel
              sx={{
                px: { xs: 0, md: 1 },

                // 1) Put the connector exactly through the CENTER of your 44px circle
                "& .MuiStepConnector-root": {
                  top: 22, // 44/2 => center line
                  zIndex: 0,
                },

                // 2) Smooth consistent line
                "& .MuiStepConnector-line": {
                  borderTopWidth: 2,
                  borderTopStyle: "solid",
                  borderColor: "rgba(200, 183, 157, 0.9)",
                  borderRadius: 999,
                },

                // Keep labels style here OR remove it (since StepLabel has its own sx)
                "& .MuiStepLabel-label": {
                  mt: 1,
                  fontWeight: 900,
                  fontSize: 13,
                  color: "#4b463f",
                  textAlign: "center",
                },
                "& .MuiStepLabel-label.Mui-active": { color: "#4b463f" },
                "& .MuiStepLabel-label.Mui-completed": { color: "#4b463f" },

                "& .MuiStep-root:last-of-type .MuiStepConnector-root": {
                  display: "none",
                },
              }}
            >
              {STEP_META.map((s, idx) => (
                <Step key={s.id}>
                  <StepButton onClick={() => goToStep(idx)} disableRipple>
                    <StepLabel
                      sx={{
                        "& .MuiStepLabel-label": {
                          color: "#4b463f",
                          fontWeight: 800,
                        },
                        "& .MuiStepLabel-label.Mui-active": {
                          color: "#4b463f",
                          fontWeight: 800,
                        },
                        "& .MuiStepLabel-label.Mui-completed": {
                          color: "#4b463f",
                          fontWeight: 800,
                        },
                      }}
                      StepIconComponent={(props) => {
                        const isActive = props.active;
                        const isDone = props.completed;

                return (
                  <Box
                            sx={{
                              mx: "auto",
                              width: 44,
                              height: 44,
                              borderRadius: 999,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",

                              // make it look like a clean white "badge"
                              bgcolor: "#fffdf9",
                              border: "1px solid #e4dbcd",
                              color: "#6b5d4a",

                              // ensure it sits above the connector line
                              position: "relative",
                              zIndex: 1,

                              // softer, more consistent shadow
                              boxShadow: "0 10px 24px rgba(75, 70, 63, 0.12)",
                              transition: "0.15s ease",
                              "& svg": { fontSize: 22 },
                            }}
                          >
                            {s.icon}
                          </Box>
                        );
                      }}
                    >
                      {t(s.label)}
                    </StepLabel>
                  </StepButton>
                </Step>
              ))}
            </Stepper>
          </Box>
          
        {/* Main content container (card-like) */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 4,
            border: "1px solid #eee7dc",
            bgcolor: "#fffdf9",
            overflow: "hidden",
          }}
        >
          <Box sx={{ p: { xs: 2, md: 2.5 } }} dir={fieldDirection}>
            {/* Plan selectors - only in Step 0 (الهوية والتواصل) */}
            {step === 0 && !isServiceCheckoutFlow && (
              <Card
                elevation={0}
                sx={{
                  mb: 2,
                  borderRadius: 1,
                  border: "1px solid #eee7dc",
                  bgcolor: "#fffdf9",
                  p: 2,
                }}
                dir={fieldDirection}
              >
                <Typography sx={{ fontWeight: 900, color: "#4b463f", mb: 1 }}>
                  {t("الخطة المختارة")}
                </Typography>

                <Grid container spacing={2}>
                  {/* Goal */}
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth dir={fieldDirection} sx={{ "& .MuiInputLabel-root": safeLabelSx }}>
                      <InputLabel sx={safeLabelSx}>{t("الهدف")}</InputLabel>
                      <Select
                        label={t("الهدف")}
                        value={selectedGoalId}
                        onChange={(e) => setSelectedGoalId(e.target.value)}
                        sx={{ borderRadius: 3, bgcolor: "#fff", direction: fieldDirection }}
                      >
                        {PLAN_GOALS.map((g) => (
                          <MenuItem key={g.id} value={g.id}>
                            {t(g.label)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Duration */}
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth dir={fieldDirection} sx={{ "& .MuiInputLabel-root": safeLabelSx }}>
                      <InputLabel sx={safeLabelSx}>{t("المدة")}</InputLabel>
                      <Select
                        label={t("المدة")}
                        value={selectedDurationId}
                        onChange={(e) => setSelectedDurationId(e.target.value)}
                        sx={{ borderRadius: 3, bgcolor: "#fff", direction: fieldDirection }}
                      >
                        {PLAN_DURATIONS.map((d) => (
                          <MenuItem key={d.id} value={d.id}>
                            {t(d.label)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                {/* Optional preview line */}
                <Box sx={{ mt: 1.2 }}>
                  <Chip
                    label={selectedPlanMeta.title ? t(selectedPlanMeta.title) : t("خطة غير متاحة")}
                    sx={{
                      bgcolor: "rgba(13, 58, 26, 0.1)",
                      border: `1px solid ${COLORS.border}`,
                      fontWeight: 800,
                    }}
                  />
                </Box>
              </Card>
            )}

            {/* Fields */}
            <Grid container spacing={2}>
              {FORM_SECTIONS[step].fields.filter(shouldRenderField).map((field) => (
                <Grid item xs={12} sm={field.type === "file" ? 12 : 6} key={field.key}>
                  {renderField(field)}
                </Grid>
              ))}
            </Grid>
          </Box>
        </Card>
          </>
        )}
      </DialogContent>

      {/* Premium footer actions */}
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 2,
          borderTop: "1px solid #eee7dc",
          bgcolor: "#fffdf9",
        }}
        dir={fieldDirection}
      >
        {isSubmitSuccess ? (
          <Stack direction="row" justifyContent="center">
            <Button
              variant="contained"
              onClick={handleClose}
              sx={{
                borderRadius: 2.5,
                fontWeight: 900,
                px: 3.2,
                textTransform: "none",
                backgroundColor: "#5a654fff",
                boxShadow: "0 10px 26px rgba(90, 101, 79, 0.24)",
                "&:hover": { backgroundColor: "#4a5441" },
              }}
            >
              {t("Close")}
            </Button>
          </Stack>
        ) : (
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Button
              onClick={handleClose}
              disabled={submitting}
              sx={{
                color: "#6b5d4a",
                fontWeight: 800,
                textTransform: "none",
              }}
            >
              {t("إلغاء")}
            </Button>

            <Stack direction="row" spacing={1}>
              {step > 0 && (
                <Button
                  onClick={handleBack}
                  disabled={submitting}
                  variant="outlined"
                  sx={{
                    borderRadius: 2.5,
                    fontWeight: 900,
                    px: 2.2,
                    textTransform: "none",
                    borderColor: "#d7c7b3",
                    color: "#4b463f",
                    "&:hover": { borderColor: "#c2a47e" },
                  }}
                >
                  {t("السابق")}
                </Button>
              )}

              {step < FORM_SECTIONS.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={submitting}
                  sx={{
                    borderRadius: 2.5,
                    fontWeight: 900,
                    px: 2.6,
                    textTransform: "none",
                    backgroundColor: "#737c6aff",
                    boxShadow: "0 10px 26px rgba(194, 164, 126, 0.28)",
                    "&:hover": { backgroundColor: "#5a654fff" },
                  }}
                >
                  {t("التالي")}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={submitting}
                  sx={{
                    borderRadius: 2.5,
                    fontWeight: 900,
                    px: 2.6,
                    textTransform: "none",
                    backgroundColor: "#737c6aff",
                    boxShadow: "0 10px 26px rgba(194, 164, 126, 0.28)",
                    "&:hover": { backgroundColor: "#5a654fff" },
                  }}
                >
                  {t("إرسال النموذج")}
                </Button>
              )}
            </Stack>
          </Stack>
        )}
      </Box>
    </Dialog>
  );
}

const nutritionistProfile = {
  name: 'Samah',
  title: 'Diététicienne & coach santé',
  pitch:
    'Spécialisée en rééquilibrage, cas métaboliques et accompagnement mindset. Plans personnalisés, validés selon vos contraintes et votre rythme.',
  bioLines: [
    'أنا سماح، أخصائية تغذية، أعمل منذ 2023 وأواصل دراستي في جامعة دولية لمواكبة التطورات المستمرة في مجال التغذية.',
    'منذ بدايتي، ساعدت أكثر من 500 شخص على تغيير حياتهم 180 درجة: أشخاص تمكنوا من خسارة وزنهم واستعادة ثقتهم بأنفسهم، وآخرون استطاعوا زيادة وزنهم بطريقة صحية.',
    'نساء عانين من اضطرابات هرمونية وتمكنّ من تحسين فرص الحمل، وحالات صحية مختلفة تحسنت مؤشراتهم الحيوية ونمط حياتهم بشكل ملحوظ.',
    'أتابعك منذ أول معاينة لأفهم كل تفاصيلك ونمط حياتك، وأكون معك في كل خطوة، خاصة في لحظات التعب أو الإحباط.',
    'أحرص على تسهيل رحلتك من كل الجوانب المادية والمعنوية والعملية لتصل لهدفك براحة ودون ضغط.',
    'طريقتي وشعاري هو التوازن: لا يجب أن تكون كل أيامك مثالية، المهم أن تظل مستمرًا.',
    'فهكذا تتحقق النتائج الحقيقية.'
  ],
  stats: [
    { label: 'Vies transformées', value: '+500' },
    { label: 'Années d’expérience', value: '3 ans' },
    { label: '', value: 'Français / Arabe / Anglais / Turc' }
  ]
};

export default function TutorSamah() {
  const { t } = useTranslation();
  const embedUrl = toEmbedUrl(SAMPLE_VIDEO);
  const selectedGoal = 'perte';
  const selectedDuration = '3m';
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [intakePlan, setIntakePlan] = useState(null);
  const [teacherUid, setTeacherUid] = useState('');
  const [teacherServices, setTeacherServices] = useState([]);
  const [loadingTeacherServices, setLoadingTeacherServices] = useState(false);
  const [studentGeoCountry, setStudentGeoCountry] = useState("");
  const [studentRegionKey, setStudentRegionKey] = useState("rest_world");
  const [serviceCheckoutError, setServiceCheckoutError] = useState("");
  const [serviceCheckoutSuccess, setServiceCheckoutSuccess] = useState("");
  const [selectedProof, setSelectedProof] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const resolveTeacherUid = async () => {
      try {
        const preferredUid = (TEACHER_UID_FALLBACK || "").trim();
        if (preferredUid) {
          const byUidSnap = await getDocs(
            query(collection(db, "teachers"), where("uid", "==", preferredUid))
          );
          if (!cancelled && !byUidSnap.empty) {
            setTeacherUid(preferredUid);
            return;
          }
        }

        const coachEmail = (TEACHER_EMAIL || '').trim().toLowerCase();
        if (!coachEmail) {
          if (!cancelled) setTeacherUid('');
          return;
        }

        const teacherQuery = query(
          collection(db, 'teachers'),
          where('email', '==', coachEmail)
        );
        const teacherSnap = await getDocs(teacherQuery);
        if (cancelled) return;

        const resolvedUid = teacherSnap.docs[0]?.data()?.uid || preferredUid;
        setTeacherUid(resolvedUid);

        if (!resolvedUid) {
          console.warn(`No teacher UID found for coach email: ${coachEmail}`);
        }
      } catch (err) {
        console.error('Failed to resolve coach account for Be Healthy landing page', err);
        if (!cancelled) setTeacherUid('');
      }
    };

    resolveTeacherUid();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const resolveStudentRegion = async () => {
      const applyCountry = (rawCountry) => {
        const candidateCountry = String(rawCountry || "").trim();
        const isUnknown = !candidateCountry || candidateCountry.toLowerCase() === "unknown";
        const fallbackCountry = inferCountryFromClientContext();
        const finalCountry = isUnknown ? fallbackCountry : candidateCountry;

        setStudentGeoCountry(finalCountry || "");
        setStudentRegionKey(resolveRegionKeyFromCountryName(finalCountry));
      };

      try {
        const baseUrl = resolveApiBaseUrl();
        const endpoint = `${baseUrl}/api/geo/price`;

        const response = await fetch(endpoint);
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          if (!cancelled) applyCountry("");
          return;
        }
        if (cancelled) return;

        applyCountry(payload?.country || "");
      } catch (error) {
        console.error("Unable to resolve student geo region", error);
        if (!cancelled) applyCountry("");
      }
    };

    resolveStudentRegion();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadTeacherServices = async () => {
      if (!teacherUid) {
        setTeacherServices([]);
        return;
      }

      try {
        setLoadingTeacherServices(true);
        const teacherQuery = query(collection(db, 'teachers'), where('uid', '==', teacherUid));
        const teacherSnap = await getDocs(teacherQuery);
        if (teacherSnap.empty) {
          if (!cancelled) setTeacherServices([]);
          return;
        }

        const teacherDocId = teacherSnap.docs[0].id;
        const servicesSnap = await getDocs(collection(db, 'teachers', teacherDocId, 'services'));

        const services = await Promise.all(
          servicesSnap.docs.map(async (serviceDoc) => {
            const serviceData = serviceDoc.data() || {};
            const status = String(serviceData.status || "published").toLowerCase();
            if (status === "archived") return null;

            const pricesSnap = await getDocs(
              collection(db, 'teachers', teacherDocId, 'services', serviceDoc.id, 'prices')
            );
            const activePrices = pricesSnap.docs
              .map((priceDoc) => priceDoc.data() || {})
              .filter((price) => price.active !== false && Number(price.amount || 0) > 0);

            if (!activePrices.length) return null;

            const normalizedStudentRegion = normalizeRegionKey(studentRegionKey);
            const exactRegionMatch = activePrices.find(
              (price) => normalizeRegionKey(price.regionKey) === normalizedStudentRegion
            );
            const turkeyCurrencyMatch =
              !exactRegionMatch && normalizedStudentRegion === "turkey"
                ? activePrices.find((price) => normalizeCurrencyKey(price.currency) === "TRY")
                : null;
            const restWorldMatch = activePrices.find(
              (price) => normalizeRegionKey(price.regionKey) === "rest_world"
            );
            const matchedPrice =
              exactRegionMatch ||
              turkeyCurrencyMatch ||
              restWorldMatch ||
              activePrices[0];

            return {
              id: serviceDoc.id,
              name: serviceData.name || "Service",
              description: serviceData.description || "",
              matchedPrice,
            };
          })
        );

        if (!cancelled) {
          setTeacherServices(services.filter(Boolean));
        }
      } catch (error) {
        console.error("Unable to load teacher services for landing page", error);
        if (!cancelled) setTeacherServices([]);
      } finally {
        if (!cancelled) setLoadingTeacherServices(false);
      }
    };

    loadTeacherServices();
    return () => {
      cancelled = true;
    };
  }, [teacherUid, studentRegionKey]);

  const handleServiceCheckout = async (service, intakeId = null, throwOnError = false) => {
    if (!teacherUid || !service?.id) {
      setServiceCheckoutError(t("Unable to identify this service checkout."));
      return;
    }

    try {
      setServiceCheckoutError("");
      const baseUrl = resolveApiBaseUrl();
      const endpoint = `${baseUrl}/api/stripe/checkout/service-session`;
      const returnPath = window.location.pathname || "/TutorSamah";

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          teacherUid,
          serviceId: service.id,
          intakeId: intakeId || null,
          clientCountry: studentGeoCountry || null,
          regionKey: studentRegionKey,
          successUrl: `${returnPath}?checkout=success&service=${service.id}${intakeId ? `&intake=${intakeId}` : ""}`,
          cancelUrl: `${returnPath}?checkout=cancel&service=${service.id}${intakeId ? `&intake=${intakeId}` : ""}`,
        }),
      });

      const contentType = response.headers.get("content-type") || "";
      let payload = null;
      if (contentType.includes("application/json")) {
        payload = await response.json();
      } else {
        const raw = await response.text();
        throw new Error(
          raw.includes("<!DOCTYPE")
            ? t("API endpoint returned HTML. Check your API base URL configuration.")
            : t("Unexpected API response format.")
        );
      }
      if (!response.ok || !payload?.ok || !payload?.url) {
        throw new Error(payload?.error || t("Unable to start checkout for this service."));
      }

      window.location.assign(payload.url);
    } catch (error) {
      console.error(error);
      setServiceCheckoutError(error.message || t("Unable to start checkout for this service."));
      if (throwOnError) throw error;
    }
  };

  const openServiceIntakeForm = (service) => {
    setServiceCheckoutError("");
    setServiceCheckoutSuccess("");
    setIntakePlan({
      planId: `${selectedGoal}-${selectedDuration}`,
      goalId: selectedGoal,
      durationId: selectedDuration,
      title: service?.name || t("Service"),
      serviceId: service?.id || null,
      serviceName: service?.name || "",
      matchedPrice: service?.matchedPrice || null,
    });
    setIntakeOpen(true);
  };

  const handleIntakeSubmitSuccess = async ({ intakeId, serviceId }) => {
    if (!serviceId) return;
    const selectedService = teacherServices.find((item) => item.id === serviceId);
    if (!selectedService) {
      throw new Error(t("Unable to identify this service checkout."));
    }

    if (studentRegionKey === "algeria") {
      const baseUrl = resolveApiBaseUrl();
      const endpoint = `${baseUrl}/api/program-intakes/send-algeria-payment-email`;
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          intakeId,
          teacherUid,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload?.ok) {
        throw new Error(payload?.error || t("Unable to send payment instructions email."));
      }

      setIntakeOpen(false);
      setServiceCheckoutSuccess(t("Form submitted successfully. We sent you CCP transfer instructions by email."));
      return;
    }

    await handleServiceCheckout(selectedService, intakeId || null, true);
  };

  const openIntakeForm = () => {
    const servicesSection = document.getElementById("services");
    if (servicesSection) {
      servicesSection.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    window.location.hash = "services";
  };


  return (
    <Box sx={{ backgroundColor: COLORS.light, minHeight: '100vh' }}>
      <LandingPageNavbar />

      <Box
        sx={{
          backgroundColor: '#737f69',
          color: '#4c433a',
          py: { xs: 7, md: 10 },
          borderBottom: '1px solid #d3d3be'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  backgroundColor: '#737f69',
                  borderRadius: 4,
                  p: { xs: 1, md: 2 },
                  textAlign: { xs: 'left', md: 'center' }
                }}
              >
                <Stack spacing={3}>
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    justifyContent={{ xs: 'flex-start', md: 'center' }}
                  >
                    <Chip
                      label={t("Be Healthy with Samah")}
                      sx={{
                        backgroundColor: '#e9e6e6ff',
                        color: '#6b5d4a',
                        fontWeight: 700,
                        letterSpacing: 0.3
                      }}
                    />
                  </Stack>

                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      lineHeight: 1.12,
                      fontSize: { xs: '2.2rem', md: '3rem' },
                      fontFamily: '"Playfair Display", "Times New Roman", serif',
                      color: '#ffffffff'
                    }}
                  >
                    {t(
                      'معنا ستتغير نظرتك للأكل الصحي والريجيم، وستبني علاقة صحية ومتوازنة مع الطعام.'
                    )}
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: '#e0dedbff',
                      fontSize: '1.05rem',
                      maxWidth: 720,
                      mx: { xs: 0, md: 'auto' }
                    }}
                  >
                    {t(
                      'Diagnostic personnalisé, plans adaptés à votre rythme et suivi rapproché pour atteindre vos objectifs (perte, prise ou accompagnement médical) sans sacrifier le plaisir de manger.'
                    )}
                  </Typography>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent={{ md: 'center' }}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={openIntakeForm}
                      sx={{
                        backgroundColor: '#fbfbfbff',
                        color: '#040303ff',
                        px: 3.5,
                        py: 1.4,
                        fontWeight: 700,
                        borderRadius: 999,
                        boxShadow: '0 12px 30px rgba(77, 65, 53, 0.25)',
                        textTransform: 'none',
                        '&:hover': { backgroundColor: '#ffffffff' }
                      }}
                    >
                      {t("Remplir le formulaire")}
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      component="a"
                      href="#video"
                      sx={{
                        color: '#ffffffff',
                        borderColor: '#cbbfae',
                        px: 3.5,
                        py: 1.3,
                        fontWeight: 700,
                        borderRadius: 999,
                        textTransform: 'none',
                        '&:hover': { borderColor: '#b9ad9b', backgroundColor: '#f4ede2' }
                      }}
                      startIcon={<PlayCircleOutlineRoundedIcon />}
                    >
                      {t("Voir la vidéo")}
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  backgroundColor: '#737f69',
                  borderRadius: 4,
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    backgroundColor: '#f1e9dc',
                    overflow: 'hidden',
                    boxShadow: '0 18px 45px rgba(77, 65, 53, 0.18)',
                    borderRadius: 999,
                  }}
                >
                  <Card
                    elevation={0}
                    sx={{
                      overflow: 'hidden',
                      background: '#fff8ee',
                      border: '1px solid #eadfce',
                      borderRadius: 999,
                    }}
                  >
                    <CardMedia
                      component="img"
                      image={AdminIcon}
                      alt={t("Programme santé")}
                      sx={{ height: { xs: 280, md: 380 }, objectFit: 'cover' }}
                    />
                  </Card>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container>
        <Box
          sx={{
            py: { xs: 4, md: 6 },
          }}
        >
          <Stack spacing={1.5} alignItems="center" textAlign="center" sx={{ maxWidth: 760, mx: 'auto' }}>
            <Typography
              variant="overline"
              sx={{ fontWeight: 800, letterSpacing: 1, color: '#9b8f7b' }}
            >
              {t("Qui vous coachera ?")}
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                fontFamily: '"Playfair Display", "Times New Roman", serif',
                color: '#4b463f'
              }}
            >
              {nutritionistProfile.name}
            </Typography>
            <Typography variant="subtitle1" sx={{ color: '#8a7c6a', fontWeight: 600 }}>
              {t(nutritionistProfile.title)}
            </Typography>
            <Typography variant="body1" sx={{ color: '#6b5d4a' }}>
              {t(nutritionistProfile.pitch)}
            </Typography>
          </Stack>

          <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center" sx={{ mt: { xs: 3, md: 5 } }}>
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  width: { xs: 260, sm: 320, md: 360 },
                  height: { xs: 260, sm: 320, md: 360 },
                  borderRadius: '50%',
                  backgroundImage: `url(${AdminIcon})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  mx: { xs: 'auto', md: 0 },
                  boxShadow: '0 18px 40px rgba(75, 70, 63, 0.18)',
                  border: '10px solid #f6f1e8'
                }}
              />
            </Grid>

            <Grid item xs={12} md={7}>
              <Box>
                <Stack spacing={1.2} sx={{ mb: 3 }}>
                  {nutritionistProfile.bioLines.map((line) => (
                    <Typography variant="body2" key={line} sx={{ color: '#6b5d4a' }}>
                      {t(line)}
                    </Typography>
                  ))}
                </Stack>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Stack direction="row" spacing={1.2} alignItems="center">
                      <MilitaryTechRoundedIcon sx={{ color: '#b9ad9b' }} />
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#4b463f' }}>
                          {t(nutritionistProfile.stats[1].value)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#8a7c6a' }}>
                          {t(nutritionistProfile.stats[1].label)}
                        </Typography>
                      </Box>
                    </Stack>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Stack direction="row" spacing={1.2} alignItems="center">
                      <PeopleAltRoundedIcon sx={{ color: '#b9ad9b' }} />
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#4b463f' }}>
                          {t(nutritionistProfile.stats[0].value)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#8a7c6a' }}>
                          {t(nutritionistProfile.stats[0].label)}
                        </Typography>
                      </Box>
                    </Stack>
                  </Grid>

                  <Grid item xs={12} sm={12} md={6}>
                    <Stack direction="row" spacing={1.2} alignItems="center">
                      <VerifiedRoundedIcon sx={{ color: '#b9ad9b' }} />
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 800,
                            color: '#4b463f',
                            lineHeight: 1.35
                          }}
                        >
                          {t(nutritionistProfile.stats[2].value)}
                        </Typography>
                        {nutritionistProfile.stats[2].label ? (
                          <Typography variant="body2" sx={{ color: '#8a7c6a' }}>
                            {t(nutritionistProfile.stats[2].label)}
                          </Typography>
                        ) : null}
                      </Box>
                    </Stack>
                  </Grid>
                </Grid>

              </Box>
            </Grid>
          </Grid>
        </Box>
      </Container>

      <Container maxWidth="lg" sx={{ py: { xs: 7, md: 10 } }} id="video">
        <Box
          sx={{
            borderRadius: { xs: 4, md: 5 },
            boxShadow: '0 24px 50px rgba(75, 70, 63, 0.12)',
            overflow: 'hidden'
          }}
        >
          <Box>
            <Box
              sx={{
                borderRadius: { xs: 3, md: 4 },
                overflow: 'hidden',
                boxShadow: '0 18px 40px rgba(75, 70, 63, 0.16)',
                backgroundColor: '#0f172a',
                mb: 2,
              }}
            >
              {embedUrl ? (
                <Box
                  component="iframe"
                  title={t("Présentation Samah")}
                  src={embedUrl}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  sx={{ width: '100%', height: { xs: 280, sm: 300, md: 460 }, border: 'none' }}
                />
              ) : (
                <Box
                  sx={{
                    color: '#fff',
                    p: 4,
                    textAlign: 'center'
                  }}
                >
                  {t("Vidéo à venir")}
                </Box>
              )}
            </Box>
          </Box>

          <Grid
            container
            spacing={0}
            sx={{
              backgroundColor: '#fff',
              textAlign: 'center'
            }}
          >
            {VIDEO_STATS.map((stat, index) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={3}
                key={stat.id}
                sx={{
                  py: { xs: 2.5, sm: 3 },
                  px: { xs: 2, sm: 3 },
                  borderRight: {
                    xs: 'none',
                    md: index < VIDEO_STATS.length - 1 ? '1px solid #eee7dc' : 'none'
                  },
                  borderBottom: {
                    xs: index < VIDEO_STATS.length - 1 ? '1px solid #eee7dc' : 'none',
                    sm: index < 2 ? '1px solid #eee7dc' : 'none',
                    md: 'none'
                  }
                }}
              >
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#4b463f' }}>
                  {t(stat.value)}
                </Typography>
                <Typography variant="body2" sx={{ color: '#9b8f7b', mt: 0.5 }}>
                  {t(stat.label)}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>

      {SHOW_RECIPES_SECTION && (
        <Box sx={{ py: { xs: 3, md: 3 }, backgroundColor: '#fbf9f4' }}>
          <Container>
            <Stack spacing={1.2} alignItems="center" textAlign="center" sx={{ mb: { xs: 4, md: 6 } }}>
              <Box
                sx={{
                  width: 54,
                  height: 54,
                  borderRadius: '50%',
                  backgroundColor: '#f1f5ed',
                  display: 'grid',
                  placeItems: 'center',
                  boxShadow: '0 8px 18px rgba(75, 70, 63, 0.08)'
                }}
              >
                <SpaRoundedIcon sx={{ color: '#6fb36f', fontSize: 28 }} />
              </Box>
              <Typography
                variant="overline"
                sx={{ fontWeight: 800, letterSpacing: 2, color: '#6b5d4a' }}
              >
                {t("Our recipes")}
              </Typography>
              <Typography variant="body2" sx={{ color: '#9b8f7b', maxWidth: 540 }}>
                {t(
                  "Search 2 million recipes using keywords, 28 nutrients and 40 diet and health filters"
                )}
              </Typography>
            </Stack>

            <Grid container spacing={{ xs: 3, md: 4 }} justifyContent="center">
              {RECIPE_CARDS.map((recipe) => (
                <Grid item xs={12} sm={6} md={4} key={recipe.id}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 4,
                      border: '1px solid #eee7dc',
                      backgroundColor: '#fffdf9',
                      boxShadow: '0 18px 40px rgba(75, 70, 63, 0.08)',
                      px: { xs: 2, sm: 3 },
                      pt: { xs: 4, sm: 5 },
                      pb: { xs: 3, sm: 4 },
                      textAlign: 'center'
                    }}
                  >
                    <Box
                      sx={{
                        width: 150,
                        height: 150,
                        borderRadius: '50%',
                        backgroundImage: `url(${recipe.image})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        mx: 'auto',
                        border: '8px solid #f7f1e7',
                        boxShadow: '0 10px 24px rgba(75, 70, 63, 0.18)'
                      }}
                    />

                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ mt: 2.5 }}
                    >
                      <Stack direction="row" spacing={0.3}>
                        {[0, 1, 2, 3].map((index) => (
                          <StarRoundedIcon key={`star-${recipe.id}-${index}`} sx={{ color: '#86b95b' }} />
                        ))}
                        <StarBorderRoundedIcon sx={{ color: '#86b95b' }} />
                      </Stack>
                      <Stack direction="row" spacing={1}>
                        <IconButton size="small" sx={{ color: '#b3a898' }}>
                          <ShareOutlinedIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" sx={{ color: '#b3a898' }}>
                          <BookmarkBorderRoundedIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Stack>

                    <Typography variant="h6" sx={{ mt: 2, fontWeight: 700, color: '#4b463f' }}>
                      {t(recipe.title)}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, color: '#9b8f7b' }}>
                      {t(recipe.description)}
                    </Typography>

                    <Divider sx={{ my: 2.5, borderColor: '#efe7dc' }} />

                    <Grid container spacing={1} justifyContent="center">
                      <Grid item xs={4}>
                        <Stack spacing={0.6} alignItems="center">
                          <AccessTimeRoundedIcon sx={{ color: '#c1b6a6' }} />
                          <Typography variant="caption" sx={{ color: '#7c6f5d', fontWeight: 600 }}>
                            {t(recipe.minutes)}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={4}>
                        <Stack spacing={0.6} alignItems="center">
                          <RestaurantMenuRoundedIcon sx={{ color: '#c1b6a6' }} />
                          <Typography variant="caption" sx={{ color: '#7c6f5d', fontWeight: 600 }}>
                            {t(recipe.difficulty)}
                          </Typography>
                        </Stack>
                      </Grid>
                      <Grid item xs={4}>
                        <Stack spacing={0.6} alignItems="center">
                          <PeopleAltRoundedIcon sx={{ color: '#c1b6a6' }} />
                          <Typography variant="caption" sx={{ color: '#7c6f5d', fontWeight: 600 }}>
                            {t(recipe.servings)}
                          </Typography>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      )}

      <Box sx={{ backgroundColor: '#f5f3ef', py: { xs: 7, md: 10 } }}>
        <Container maxWidth="lg">
          <Stack spacing={1.4} alignItems={{ xs: 'flex-start', md: 'center' }} sx={{ mb: { xs: 4, md: 6 } }}>
            <Chip
              label={t("Transformations réelles")}
              sx={{
                backgroundColor: '#e9dfd0',
                color: '#6b5d4a',
                fontWeight: 700,
                letterSpacing: 0.2
              }}
            />
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: '#2e2f3a',
                textAlign: { md: 'center' },
                fontFamily: '"Playfair Display", "Times New Roman", serif'
              }}
            >
              {t("Leurs résultats parlent d’eux-mêmes")}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#7a6e5f',
                maxWidth: 760,
                textAlign: { md: 'center' }
              }}
            >
              {t("Profils accompagnés et retours partagés directement par nos clients.")}
            </Typography>
          </Stack>

          <Grid container spacing={2.2} sx={{ mb: { xs: 3, md: 4 } }}>
            {testimonials.map((item, index) => (
              <Grid
                item
                xs={12}
                sm={testimonials.length % 2 === 1 && index === testimonials.length - 1 ? 12 : 6}
                md={getClientCardMd(index, testimonials.length)}
                key={item.id}
              >
                <Card
                  sx={{
                    borderRadius: 3,
                    border: '1px solid #e7ddd0',
                    backgroundColor: '#fff',
                    boxShadow: '0 10px 24px rgba(75, 70, 63, 0.08)',
                    p: 1.2,
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 800, color: '#3f372f', px: 0.8, pb: 1.1 }}
                  >
                    {t(item.name)}
                  </Typography>

                  {item.before && item.after ? (
                    <Grid container spacing={1.1} sx={{ height: { xs: 290, sm: 320, md: 300 } }}>
                      <Grid item xs={6} sx={{ display: 'flex' }}>
                        <CardActionArea
                          onClick={() =>
                            setSelectedProof({ type: 'image', src: `${CLIENT_REVIEWS_BASE}/${item.before}` })
                          }
                          sx={{ borderRadius: 2.3, overflow: 'hidden', height: '100%', width: '100%' }}
                        >
                          <Box sx={{ position: 'relative', height: '100%' }}>
                            <Box
                              component="img"
                              src={`${CLIENT_REVIEWS_BASE}/${item.before}`}
                              alt={`${t(item.name)} - ${t("Avant")}`}
                              loading="lazy"
                              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            />
                            <Chip
                              label={t("Avant")}
                              size="small"
                              sx={{
                                position: 'absolute',
                                left: 8,
                                top: 8,
                                backgroundColor: 'rgba(0,0,0,0.65)',
                                color: '#fff',
                                fontWeight: 700
                              }}
                            />
                          </Box>
                        </CardActionArea>
                      </Grid>

                      <Grid item xs={6} sx={{ display: 'flex' }}>
                        <CardActionArea
                          onClick={() =>
                            setSelectedProof({ type: 'image', src: `${CLIENT_REVIEWS_BASE}/${item.after}` })
                          }
                          sx={{ borderRadius: 2.3, overflow: 'hidden', height: '100%', width: '100%' }}
                        >
                          <Box sx={{ position: 'relative', height: '100%' }}>
                            <Box
                              component="img"
                              src={`${CLIENT_REVIEWS_BASE}/${item.after}`}
                              alt={`${t(item.name)} - ${t("Après")}`}
                              loading="lazy"
                              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            />
                            <Chip
                              label={t("Après")}
                              size="small"
                              sx={{
                                position: 'absolute',
                                left: 8,
                                top: 8,
                                backgroundColor: 'rgba(20, 83, 45, 0.78)',
                                color: '#fff',
                                fontWeight: 700
                              }}
                            />
                          </Box>
                        </CardActionArea>
                      </Grid>
                    </Grid>
                  ) : (
                    <CardActionArea
                      onClick={() =>
                        setSelectedProof({ type: 'image', src: `${CLIENT_REVIEWS_BASE}/${item.combined}` })
                      }
                      sx={{
                        borderRadius: 2.3,
                        overflow: 'hidden',
                        height: { xs: 290, sm: 320, md: 300 }
                      }}
                    >
                      <Box sx={{ position: 'relative', height: '100%' }}>
                        <Box
                          component="img"
                          src={`${CLIENT_REVIEWS_BASE}/${item.combined}`}
                          alt={`${t(item.name)} - ${t("Avant / Après")}`}
                          loading="lazy"
                          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                        <Chip
                          label={t("Avant / Après")}
                          size="small"
                          sx={{
                            position: 'absolute',
                            left: 8,
                            top: 8,
                            backgroundColor: 'rgba(0,0,0,0.62)',
                            color: '#fff',
                            fontWeight: 700
                          }}
                        />
                      </Box>
                    </CardActionArea>
                  )}
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box
            sx={{
              columnCount: { xs: 1, sm: 2, md: 3 },
              columnGap: { xs: 2, md: 2.5 }
            }}
          >
            <Box
              sx={{
                breakInside: 'avoid',
                mb: { xs: 2, md: 2.5 }
              }}
            >
              <CardActionArea
                onClick={() => setSelectedProof({ type: 'video', src: CLIENT_PROOF_VIDEO })}
                sx={{
                  borderRadius: 4,
                  overflow: 'hidden',
                  border: '1px solid #dfd4c4',
                  boxShadow: '0 20px 38px rgba(55, 46, 38, 0.12)'
                }}
              >
                <Box sx={{ position: 'relative', backgroundColor: '#111827' }}>
                  <Box
                    component="video"
                    src={CLIENT_PROOF_VIDEO}
                    poster={WRITTEN_REVIEWS[0].src}
                    muted
                    autoPlay
                    loop
                    playsInline
                    preload="metadata"
                    sx={{
                      width: '100%',
                      maxHeight: { xs: 430, md: 540 },
                      objectFit: 'cover',
                      display: 'block'
                    }}
                  />

                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-end',
                      p: 2.2,
                      gap: 1,
                      background: 'linear-gradient(180deg, rgba(0,0,0,0.08) 25%, rgba(0,0,0,0.65) 100%)'
                    }}
                  >
                    <Chip
                      label={t("Témoignage vidéo")}
                      sx={{
                        alignSelf: 'flex-start',
                        backgroundColor: 'rgba(255,255,255,0.92)',
                        color: '#3d362f',
                        fontWeight: 800
                      }}
                    />
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PlayCircleOutlineRoundedIcon sx={{ color: '#fff', fontSize: 30 }} />
                      <Typography sx={{ color: '#fff', fontWeight: 700 }}>
                        {t("Voir en plein écran")}
                      </Typography>
                    </Stack>
                  </Box>
                </Box>
              </CardActionArea>
            </Box>

            {WRITTEN_REVIEWS.map((review, index) => (
              <Box
                key={review.id}
                sx={{
                  breakInside: 'avoid',
                  mb: { xs: 2, md: 2.5 }
                }}
              >
                <CardActionArea
                  onClick={() => setSelectedProof({ type: 'image', src: review.src })}
                  sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                    border: '1px solid #e7dfd2',
                    boxShadow: '0 12px 28px rgba(75, 70, 63, 0.10)',
                    '&:hover img': { transform: 'scale(1.03)' },
                    '&:hover .written-review-overlay': { opacity: 1 }
                  }}
                >
                  <Box sx={{ position: 'relative', backgroundColor: '#ffffff' }}>
                    <Box
                      component="img"
                      src={review.src}
                      alt={`${t("Retour client")} ${index + 1}`}
                      loading="lazy"
                      sx={{
                        display: 'block',
                        width: '100%',
                        height: 'auto',
                        transition: 'transform 260ms ease'
                      }}
                    />

                    <Box
                      className="written-review-overlay"
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'grid',
                        placeItems: 'center',
                        background: 'linear-gradient(180deg, rgba(0,0,0,0.08) 0%, rgba(0,0,0,0.42) 100%)',
                        opacity: 0,
                        transition: 'opacity 220ms ease'
                      }}
                    >
                      <Chip
                        label={t("Ouvrir")}
                        sx={{
                          backgroundColor: 'rgba(255,255,255,0.92)',
                          color: '#3d362f',
                          fontWeight: 700
                        }}
                      />
                    </Box>
                  </Box>
                </CardActionArea>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }} id="services">
        <Stack spacing={2.2} sx={{ mb: 3, textAlign: 'center' }} alignItems="center">
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              color: '#4b463f',
              fontFamily: '"Playfair Display", "Times New Roman", serif',
              fontSize: { xs: '2rem', md: '2.4rem' },
            }}
          >
            {t("Services")}
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: '#8a7c6a', maxWidth: 760, fontSize: { xs: '1rem', md: '1.14rem' } }}
          >
            {t("Choose the service that fits your goal and start your personalized follow-up.")}
          </Typography>
        </Stack>

        {serviceCheckoutError ? (
          <Alert
            severity="warning"
            onClose={() => setServiceCheckoutError("")}
            sx={{ mb: 2, borderRadius: 2 }}
          >
            {serviceCheckoutError}
          </Alert>
        ) : null}
        {serviceCheckoutSuccess ? (
          <Alert
            severity="success"
            onClose={() => setServiceCheckoutSuccess("")}
            sx={{ mb: 2, borderRadius: 2 }}
          >
            {serviceCheckoutSuccess}
          </Alert>
        ) : null}

        {loadingTeacherServices ? (
          <Stack spacing={1}>
            <LinearProgress />
            <Typography variant="body2" sx={{ color: '#8a7c6a' }}>
              {t("Loading services...")}
            </Typography>
          </Stack>
        ) : teacherServices.length === 0 ? (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            {t("No services are currently published for this teacher.")}
          </Alert>
        ) : (
          <Grid container spacing={2.2}>
            {teacherServices.map((service) => {
              const amountLabel = Number(service.matchedPrice?.amount || 0).toFixed(2);
              const currencyLabel = service.matchedPrice?.currency || "";

              return (
                <Grid item xs={12} sm={6} lg={3} key={service.id}>
                  <Card
                    elevation={0}
                    onClick={() => openServiceIntakeForm(service)}
                    sx={{
                      height: '100%',
                      borderRadius: 4,
                      border: '1px solid #e8dfd2',
                      background: 'linear-gradient(180deg, #ffffff 0%, #fdfaf6 100%)',
                      boxShadow: '0 10px 24px rgba(75, 70, 63, 0.10)',
                      cursor: 'pointer',
                      transition: 'transform 170ms ease, box-shadow 170ms ease',
                      '&:hover': {
                        transform: 'translateY(-3px)',
                        boxShadow: '0 16px 34px rgba(75, 70, 63, 0.14)',
                      },
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2.2, md: 2.6 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
                      <Stack spacing={1.2} sx={{ mb: 1.3 }}>
                        <Typography variant="overline" sx={{ letterSpacing: '0.08em', color: '#8f7f69', fontWeight: 800 }}>
                          {t("Service plan")}
                        </Typography>

                        <Typography variant="h5" sx={{ fontWeight: 900, color: '#2f2a24', lineHeight: 1.12 }}>
                          {service.name}
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            color: '#7a6e5f',
                            minHeight: 44,
                          }}
                        >
                          {service.description || t("Personalized coaching service with structured follow-up.")}
                        </Typography>
                      </Stack>

                      <Box
                        sx={{
                          mt: 0.4,
                          mb: 1.8,
                          p: 1.6,
                          borderRadius: 2.5,
                          border: '1px solid #e9e2d7',
                          backgroundColor: '#fffdfa',
                        }}
                      >
                        <Stack direction="row" alignItems="baseline" spacing={1}>
                          <Typography
                            variant="h3"
                            sx={{
                              fontSize: { xs: '2rem', md: '2.35rem' },
                              fontWeight: 900,
                              color: '#22201c',
                              letterSpacing: '-0.02em',
                              lineHeight: 1,
                            }}
                          >
                            {amountLabel}
                          </Typography>
                          <Typography variant="h6" sx={{ color: '#3f3a33', fontWeight: 800 }}>
                            {currencyLabel}
                          </Typography>
                        </Stack>
                        <Typography variant="caption" sx={{ color: '#8a7c6a', fontWeight: 700 }}>
                          {t("One-time payment")}
                        </Typography>
                      </Box>

                      <Button
                        variant="contained"
                        onClick={(event) => {
                          event.stopPropagation();
                          openServiceIntakeForm(service);
                        }}
                        sx={{
                          mb: 0.8,
                          width: '100%',
                          borderRadius: 3,
                          py: 1.05,
                          textTransform: 'none',
                          fontSize: '1rem',
                          fontWeight: 800,
                          backgroundColor: '#737f69',
                          '&:hover': {
                            backgroundColor: '#68745fff',
                          },
                        }}
                      >
                        {t("Start with Samah")}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Container>

      <IntakeFormDialog
        open={intakeOpen}
        onClose={() => setIntakeOpen(false)}
        teacherUid={teacherUid}
        onSubmitSuccess={handleIntakeSubmitSuccess}
        planMeta={
          intakePlan || {
            planId: `${selectedGoal}-${selectedDuration}`,
            goalId: selectedGoal,
            durationId: selectedDuration,
            title: ""
          }
        }
      />

      <Dialog
        open={Boolean(selectedProof)}
        onClose={() => setSelectedProof(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ fontWeight: 800, color: '#3f362f' }}>
          {t("Galerie clients")}
          <IconButton
            aria-label={t("Close")}
            onClick={() => setSelectedProof(null)}
            sx={{ position: 'absolute', right: 10, top: 8 }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, backgroundColor: '#0f172a' }}>
          {selectedProof?.type === 'video' && (
            <Box
              component="video"
              src={selectedProof.src}
              controls
              autoPlay
              playsInline
              preload="metadata"
              sx={{
                width: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
                display: 'block'
              }}
            />
          )}
          {selectedProof?.type === 'image' && (
            <Box
              component="img"
              src={selectedProof.src}
              alt={t("Retour client")}
              sx={{
                width: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
                display: 'block'
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
